import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { getApiBaseUrl } from "../config/api";
import RichTextEditor from "./shared/RichTextEditor";
import CaptionAssist from "./shared/CaptionAssist";

const API_URL = getApiBaseUrl();
const CATEGORIES = [
  "General",
  "Really Lesson",
  "COOL",
  "ReallyAvatar",
  "Coursera",
  "TechTree",
];

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxSizing: "border-box",
};

/**
 * LinkedIn-style article composer with TipTap + cover image.
 */
export default function ArticleForm({ user, onClose, onSuccess, onRefresh, groupId = null }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Cover must be an image.");
      return;
    }
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setCoverFile(file);
    setCoverPreview(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email && !user?.firstname) {
      alert("You must be logged in");
      return;
    }
    const plain = content.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
    if (!title.trim() || !plain) {
      alert("Please add a title and article content.");
      return;
    }

    setLoading(true);
    setStatus("Publishing article...");
    try {
      const formData = new FormData();
      user.email && formData.append("email", user.email);
      user.firstname && formData.append("firstname", user.firstname);
      user.username && formData.append("username", user.username);
      user.country && formData.append("country", user.country);
      formData.append("title", title.trim());
      formData.append("content", content);
      formData.append("category", category);
      formData.append("post_type", "article");
      if (groupId) {
        formData.append("group_id", groupId);
        formData.append("user_id", user.id);
      }

      if (coverFile) {
        setStatus("Compressing cover...");
        try {
          const compressed = await imageCompression(coverFile, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          formData.append("cover", compressed, compressed.name);
        } catch {
          formData.append("cover", coverFile);
        }
      }

      setStatus("Uploading...");
      const endpoint = groupId
        ? `${API_URL}/create_group_post.php`
        : `${API_URL}/create_post.php`;
      const { data } = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      if (data.success) {
        onSuccess?.(data.post);
        onRefresh?.();
        onClose?.();
      } else {
        alert(data.message || "Failed to publish article");
      }
    } catch (err) {
      console.error(err);
      alert("Upload error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1100,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          width: "100%",
          maxWidth: 720,
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 25px 50px rgba(15,23,42,0.35)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 18,
            border: "none",
            background: "rgba(15,23,42,0.04)",
            width: 32,
            height: 32,
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: 18,
            color: "#475569",
          }}
        >
          ×
        </button>

        <h2 style={{ margin: "0 0 4px", fontSize: 18, color: "#0f172a" }}>
          Write an article
        </h2>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>
          Long-form story with formatting and an optional cover image.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Article headline"
            required
            style={inputStyle}
          />

          <div
            style={{
              border: "2px dashed #cbd5e1",
              borderRadius: 12,
              padding: 14,
              marginBottom: 12,
              textAlign: "center",
              cursor: "pointer",
              background: "#f8fafc",
            }}
            onClick={() => document.getElementById("articleCoverInput")?.click()}
          >
            {coverPreview ? (
              <img
                src={coverPreview}
                alt="Cover preview"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            ) : (
              <span style={{ fontSize: 13, color: "#64748b" }}>
                Add cover image (optional)
              </span>
            )}
            <input
              id="articleCoverInput"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleCoverChange}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <RichTextEditor content={content} onChange={setContent} />
            <CaptionAssist
              kind="article"
              format="html"
              text={content}
              title={title}
              category={category}
              userId={user?.id || user?.user_id}
              disabled={loading}
              style={{ marginTop: 8 }}
              onApply={setContent}
              onApplyTitle={(nextTitle) => {
                if (!title.trim() && nextTitle) setTitle(nextTitle);
              }}
            />
          </div>

          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ ...inputStyle, backgroundColor: "#f8fafc" }}
          >
            {CATEGORIES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 4,
              background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "12px 20px",
              width: "100%",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {loading ? status || "Publishing..." : "Publish article"}
          </button>
        </form>
      </div>
    </div>
  );
}
